import { AvailableNow } from 'store/resource/types';
import { formatTime24 } from 'shared/utils/time';
import { ACTION_STATUSES, TRACK_EVENT_NAMES, TRACK_EVENT_TYPES } from 'shared/consts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { ONE_HOUR } from 'components/schedule-select/const';
import { push } from 'store/router/actions';
import qs from 'qs';

import { resourcesState, selectGetResourcesLoadingStatus } from 'store/resources/selectors';
import { searchParams } from 'pages/date-select-page/interface';
import { track } from '@hqo/web-tracking';
import { useBooleanState } from '@hqo/react-components-library/dist/hooks';
import { useLocale } from '../../../hooks/use-locale.hook';
import { useOwnerParams } from 'hooks/use-owner-params.hook';
import { useSearchParams } from '../../../hooks/use-search-params.hook';
import { resetPrice } from 'store/price/actions';

import { getResource } from 'store/resource/actions';
import { resourceState, selectGetResourceStatus } from 'store/resource/selectors';
import { useOnSubmitBookingRequest } from 'pages/schedule-select-page/components/use-on-submit-booking-request.hook';
import { useParams } from 'react-router-dom';
import { selectCurrentRoomLayout } from 'store/room-layout/selectors';
import { selectAllAddOns } from 'store/add-ons/selectors';
import { useAppInstanceConfigFeature } from 'hooks/use-app-instance-config-feature.hook';
import { usePriceQuery } from 'data/queries/getPriceQuery.hook';

interface UseAvailableNowReturnValues {
  hasAvailableResources: boolean;
  isGetResourcesLoading: boolean;
  calculateAvailableNowEndTime: (
    availableNow: AvailableNow,
    minimumDuration: number,
    maximumDuration: number,
  ) => string;
  onAvailableNowBlockClick: (
    uuid: string,
    availableNow: AvailableNow,
    minimumDuration: number,
    maximumDuration: number,
    resourceType: string,
  ) => void;
}

export const useAvailableNow = (): UseAvailableNowReturnValues => {
  const { search } = useLocation();
  const locale = useLocale();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { ownerType, ownerUuid } = useOwnerParams();
  const queryParams = useSearchParams<searchParams>();
  const onSubmitBookingRequest = useOnSubmitBookingRequest();
  const { isCompactUiEnabled } = useAppInstanceConfigFeature();
  const { resourceUuid } = useParams();

  const { resources } = useSelector(resourcesState);
  const isGetResourcesLoading = useSelector(selectGetResourcesLoadingStatus) as boolean;
  const { value: hasAvailableResources, toggle: toggleHasAvailableResources } = useBooleanState(false);
  const [isAvailableBlockClicked, setIsAvailableBlockClicked] = useState<boolean>(false);
  const { resource } = useSelector(resourceState);
  const hasAdditionalInfo = useMemo(() => !!resource?.user_prompts?.length, [resource]);
  const [startTime, setStartTime] = useState<string>(null);
  const [endTime, setEndTime] = useState<string>(null);
  const [startDate, setStartDate] = useState<string>(null);

  const getResourceStatus = useSelector(selectGetResourceStatus);
  const isPaidResource = useMemo<boolean>(
    () => !!(resource?.booking_windows?.some(({ pricing_rules }) => pricing_rules?.length) || resource?.minimum_price),
    [resource?.booking_windows, resource?.minimum_price],
  );
  const currentRoomLayout = useSelector(selectCurrentRoomLayout);
  const selectedAddOns = useSelector(selectAllAddOns);

  useEffect(() => {
    if (resources?.some(selectedResource => selectedResource.available_now) && !hasAvailableResources) {
      toggleHasAvailableResources();
    }
  }, [resources, toggleHasAvailableResources, hasAvailableResources]);

  const { isSuccess: isPriceSuccess } = usePriceQuery({
    resourceId: resource?.id,
    timezone: resource?.timezone,
    enabled: isPaidResource,
  });

  const [isSubmitBookingEnabled, setIsSubmitBookingEnabled] = useState(false);

  useEffect(() => {
    if (isSubmitBookingEnabled) {
      onSubmitBookingRequest({
        availableNowStartDate: startDate,
        availableNowStartTime: startTime,
        availableNowEndTime: endTime,
        availableNowResource: resource,
        currentRoomLayout,
        selectedAddOns,
      });
      setIsSubmitBookingEnabled(false);
      queryClient.resetQueries({ queryKey: ['price'] });
      dispatch(resetPrice());
    }
  }, [
    isSubmitBookingEnabled,
    setIsSubmitBookingEnabled,
    onSubmitBookingRequest,
    dispatch,
    queryClient,
    startDate,
    startTime,
    endTime,
    resource,
    currentRoomLayout,
    selectedAddOns,
  ]);

  useEffect(() => {
    if (getResourceStatus === ACTION_STATUSES.FULFILLED && isAvailableBlockClicked) {
      const params = {
        ...queryParams,
        startDate,
        startTime,
        endTime,
        isExact: true,
      };
      const queryString = qs.stringify(params);
      if (hasAdditionalInfo) {
        setIsAvailableBlockClicked(false);
        dispatch(push(`resource-booking/resource/${resource.uuid}/additional-information?${queryString}`));
      } else if (startDate && startTime && endTime && ((isPaidResource && isPriceSuccess) || !isPaidResource)) {
        setIsAvailableBlockClicked(false);
        setIsSubmitBookingEnabled(true);
      }
    }
  }, [
    dispatch,
    endTime,
    isPriceSuccess,
    getResourceStatus,
    hasAdditionalInfo,
    isAvailableBlockClicked,
    isPaidResource,
    onSubmitBookingRequest,
    resource?.uuid,
    search,
    startDate,
    startTime,
    queryParams,
  ]);

  const calculateAvailableNowEndTime = useCallback(
    (availableNow: AvailableNow, minimumDuration: number, maximumDuration: number) => {
      const currentAvailableStartDate = new Date(availableNow.start_at.replace(/-/g, '/'));

      if (minimumDuration && ONE_HOUR <= minimumDuration) {
        currentAvailableStartDate.setMinutes(currentAvailableStartDate.getMinutes() + minimumDuration);
      } else if (maximumDuration && ONE_HOUR >= maximumDuration) {
        currentAvailableStartDate.setMinutes(currentAvailableStartDate.getMinutes() + maximumDuration);
      } else if (ONE_HOUR >= availableNow?.available_minutes) {
        currentAvailableStartDate.setMinutes(currentAvailableStartDate.getMinutes() + availableNow?.available_minutes);
      } else {
        currentAvailableStartDate.setHours(currentAvailableStartDate.getHours() + 1);
      }

      // Internal 24h `HH:mm` handle: this endTime is serialized into the query string and passed to
      // the booking request, so it must stay locale-independent (NOT a display formatter).
      return formatTime24(currentAvailableStartDate.getHours() * 60 + currentAvailableStartDate.getMinutes());
    },
    [locale],
  );

  const onAvailableNowBlockClick = useCallback(
    (
      uuid: string,
      availableNow: AvailableNow,
      minimumDuration: number,
      maximumDuration: number,
      resourceType: string,
    ) => {
      if (isCompactUiEnabled) {
        dispatch(
          getResource.request({
            ownerUuid,
            ownerType,
            resourceUuid: uuid ?? resources.find(reduxResource => reduxResource.uuid === resourceUuid).id.toString(),
          }),
        );
      }

      const selectedAvailableNowResource = resources?.find(({ uuid: uuidFromState }) => uuidFromState === uuid);

      track(
        TRACK_EVENT_NAMES.AVAILABLE_NOW_RESOURCE_CLICK,
        {
          type: TRACK_EVENT_TYPES.ACTION,
          resource: {
            type: resourceType,
            uuid,
            resource_id: selectedAvailableNowResource?.id?.toString(),
            name: selectedAvailableNowResource?.name,
            capacity: selectedAvailableNowResource?.capacity,
          },
        },
        { sendToHqoTracking: true },
      );
      const availableEndTime = !availableNow.available_minutes
        ? null
        : calculateAvailableNowEndTime(availableNow, minimumDuration, maximumDuration);
      const currentAvailableStartArray = availableNow.start_at.split(' ');
      setEndTime(availableEndTime);
      setStartTime(currentAvailableStartArray[1]);
      setStartDate(currentAvailableStartArray[0]);

      const availableNowFilterParams = {
        ...queryParams,
        startDate: currentAvailableStartArray[0],
        startTime: currentAvailableStartArray[1],
        endTime: availableEndTime,
        isExact: true,
      };
      const queryString = qs.stringify(availableNowFilterParams);
      if (isCompactUiEnabled) {
        setIsAvailableBlockClicked(true);
      } else {
        dispatch(push(`/resource-booking/resource/${uuid}?${queryString}`));
      }
    },
    [
      isCompactUiEnabled,
      calculateAvailableNowEndTime,
      queryParams,
      dispatch,
      ownerUuid,
      ownerType,
      resources,
      resourceUuid,
    ],
  );

  return {
    hasAvailableResources,
    calculateAvailableNowEndTime,
    onAvailableNowBlockClick,
    isGetResourcesLoading,
  };
};
